import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSettings, FiCreditCard, FiCheck, FiX, FiAlertCircle, FiRefreshCw, FiLoader, FiClock, FiShield } from 'react-icons/fi';

interface PaymentSetting {
  value: any;
  description: string;
  category: string;
  type: string;
}

interface PaymentSettingsData {
  [key: string]: PaymentSetting;
}

const PaymentSettings: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettingsData>({});
  const [runtime, setRuntime] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<any>(null);
  const [paypalStatus, setPaypalStatus] = useState<any>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);

  useEffect(() => {
    fetchSettings();
    checkPayPalStatus(); // Auto-check PayPal status on load
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const PAYMENT_SERVICE_URL = process.env.REACT_APP_PAYMENT_SERVICE_URL || 'http://localhost:3015';
      
      const response = await fetch(`${PAYMENT_SERVICE_URL}/api/payment-settings`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Settings API not available. Please ensure payment-service is running.');
      }

      const data = await response.json();

      if (data.success) {
        setSettings(data.settings || {});
        setRuntime(data.runtime || {});
      } else {
        throw new Error(data.error || 'Failed to fetch settings');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to connect to payment service');
      console.error('Fetch settings error:', err);
    } finally {
      setLoading(false);
    }
  };

  const updateSetting = async (key: string, value: any) => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const PAYMENT_SERVICE_URL = process.env.REACT_APP_PAYMENT_SERVICE_URL || 'http://localhost:3015';
      
      const response = await fetch(`${PAYMENT_SERVICE_URL}/api/payment-settings/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess(`Setting updated successfully`);
        await fetchSettings(); // Reload all settings
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(data.error || 'Failed to update setting');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const testPayPalConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);
      setError(null);

      const PAYMENT_SERVICE_URL = process.env.REACT_APP_PAYMENT_SERVICE_URL || 'http://localhost:3015';
      
      const response = await fetch(`${PAYMENT_SERVICE_URL}/api/payment-settings/test-paypal`, {
        method: 'POST'
      });

      const data = await response.json();
      setTestResult(data);

      if (!data.success) {
        setError(data.message || 'PayPal connection test failed');
      }
    } catch (err: any) {
      setError(err.message);
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const checkPayPalStatus = async () => {
    try {
      setCheckingStatus(true);
      const PAYMENT_SERVICE_URL = process.env.REACT_APP_PAYMENT_SERVICE_URL || 'http://localhost:3015';
      
      const response = await fetch(`${PAYMENT_SERVICE_URL}/api/payment-settings/paypal-status`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      // Check if response is OK
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}: ${response.statusText}`);
      }

      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('API endpoint not found or returned HTML instead of JSON. Please ensure payment-service is running and settingsRoutes.js is deployed.');
      }

      const data = await response.json();
      setPaypalStatus(data);
    } catch (err: any) {
      console.error('PayPal status check failed:', err);
      setPaypalStatus({ 
        success: false, 
        error: err.message || 'Failed to check PayPal status',
        configured: false,
        connected: false,
        mode: 'unknown'
      });
    } finally {
      setCheckingStatus(false);
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingState><FiLoader className="spin" /> Loading payment settings...</LoadingState>
      </Container>
    );
  }

  const paypalMode = settings.paypal_mode?.value || 'sandbox';
  const paypalEnabled = settings.paypal_enabled?.value !== false;
  const testEnabled = settings.test_payments_enabled?.value !== false;

  return (
    <Container>
      <Header>
        <Title><FiCreditCard /> Payment Gateway Settings</Title>
        <HeaderButtons>
          <RefreshButton onClick={() => { fetchSettings(); checkPayPalStatus(); }} disabled={loading || checkingStatus}>
            <FiRefreshCw className={checkingStatus ? 'spin' : ''} /> Refresh
          </RefreshButton>
        </HeaderButtons>
      </Header>

      {/* PayPal Status Overview Card */}
      {paypalStatus && (
        <StatusOverviewCard>
          <StatusHeader>
            <StatusTitle>
              <FiShield /> PayPal Connection Status
            </StatusTitle>
            {checkingStatus ? (
              <StatusIndicator status="checking">
                <FiLoader className="spin" /> Checking...
              </StatusIndicator>
            ) : (
              <StatusIndicator status={paypalStatus.connected ? 'connected' : 'disconnected'}>
                {paypalStatus.connected ? '● Connected' : '○ Disconnected'}
              </StatusIndicator>
            )}
          </StatusHeader>
          
          <StatusGrid>
            <StatusItem>
              <StatusLabel>Configuration</StatusLabel>
              <StatusValue status={paypalStatus.configured ? 'good' : 'bad'}>
                {paypalStatus.configured ? '✓ Configured' : '✗ Not Configured'}
              </StatusValue>
            </StatusItem>
            
            <StatusItem>
              <StatusLabel>Environment</StatusLabel>
              <StatusValue status={paypalStatus.mode === 'live' ? 'warning' : 'neutral'}>
                {paypalStatus.mode === 'live' ? '🔴 Live' : 
                 paypalStatus.mode === 'sandbox' ? '🧪 Sandbox' : 
                 '⚠️ Unknown'}
              </StatusValue>
            </StatusItem>
            
            <StatusItem>
              <StatusLabel>Connection</StatusLabel>
              <StatusValue status={paypalStatus.connected ? 'good' : 'bad'}>
                {paypalStatus.connected ? '✓ Active' : '✗ Failed'}
              </StatusValue>
            </StatusItem>

            <StatusItem>
              <StatusLabel>Last Checked</StatusLabel>
              <StatusValue status="neutral">
                <FiClock size={12} /> {new Date().toLocaleTimeString()}
              </StatusValue>
            </StatusItem>
          </StatusGrid>

          {paypalStatus.error && (
            <StatusError>
              <FiAlertCircle /> {paypalStatus.error}
              {paypalStatus.error.includes('not found') && (
                <div style={{ marginTop: '8px', fontSize: '13px' }}>
                  💡 <strong>Fix:</strong> Ensure settingsRoutes.js is deployed to services/payment-service/routes/ and server.js includes the route.
                </div>
              )}
            </StatusError>
          )}
        </StatusOverviewCard>
      )}

      {error && (
        <Alert type="error">
          <FiAlertCircle /> {error}
        </Alert>
      )}

      {success && (
        <Alert type="success">
          <FiCheck /> {success}
        </Alert>
      )}

      {/* PayPal Configuration */}
      <Card>
        <CardHeader>
          <CardTitle>
            <FiCreditCard /> PayPal Configuration
          </CardTitle>
          <StatusBadge status={runtime.paypal_configured ? 'connected' : 'notConfigured'}>
            {runtime.paypal_configured ? '✓ Configured' : '⚠️ Not Configured'}
          </StatusBadge>
        </CardHeader>

        <SettingRow>
          <SettingInfo>
            <SettingLabel>PayPal Status</SettingLabel>
            <SettingDescription>Enable or disable PayPal payments</SettingDescription>
          </SettingInfo>
          <ToggleSwitch
            checked={paypalEnabled}
            disabled={saving || !runtime.paypal_configured}
          >
            <input 
              type="checkbox" 
              checked={paypalEnabled} 
              onChange={(e) => updateSetting('paypal_enabled', e.target.checked)}
              disabled={saving || !runtime.paypal_configured}
            />
            <span className="slider"></span>
          </ToggleSwitch>
        </SettingRow>

        <Divider />

        <SettingRow>
          <SettingInfo>
            <SettingLabel>PayPal Mode</SettingLabel>
            <SettingDescription>
              Sandbox for testing, Live for real transactions
              {runtime.env_mode && ` (Environment: ${runtime.env_mode})`}
            </SettingDescription>
          </SettingInfo>
          <ModeSelector>
            <ModeButton
              active={paypalMode === 'sandbox'}
              onClick={() => updateSetting('paypal_mode', 'sandbox')}
              disabled={saving}
            >
              🧪 Sandbox
            </ModeButton>
            <ModeButton
              active={paypalMode === 'live'}
              onClick={() => updateSetting('paypal_mode', 'live')}
              disabled={saving}
              warning
            >
              🔴 Live
            </ModeButton>
          </ModeSelector>
        </SettingRow>

        {runtime.paypal_configured && (
          <>
            <Divider />
            <SettingRow>
              <SettingInfo>
                <SettingLabel>Connection Test</SettingLabel>
                <SettingDescription>Verify PayPal credentials are working</SettingDescription>
              </SettingInfo>
              <TestButton onClick={testPayPalConnection} disabled={testing}>
                {testing ? <FiLoader className="spin" /> : <FiShield />}
                {testing ? 'Testing...' : 'Test Connection'}
              </TestButton>
            </SettingRow>

            {testResult && (
              <TestResult success={testResult.success}>
                {testResult.success ? (
                  <>
                    <FiCheck /> PayPal connection successful! Mode: {testResult.mode}
                  </>
                ) : (
                  <>
                    <FiX /> {testResult.error || 'Connection failed'}
                  </>
                )}
              </TestResult>
            )}
          </>
        )}
      </Card>

      {/* Test Payments */}
      <Card>
        <CardHeader>
          <CardTitle>
            🧪 Test/Mock Payments
          </CardTitle>
          <StatusBadge status={testEnabled ? 'active' : 'inactive'}>
            {testEnabled ? '✓ Enabled' : '○ Disabled'}
          </StatusBadge>
        </CardHeader>

        <SettingRow>
          <SettingInfo>
            <SettingLabel>Test Payments (Admin Only)</SettingLabel>
            <SettingDescription>
              Allow test/mock payments for development and testing. Only administrators can use test payments when enabled.
            </SettingDescription>
          </SettingInfo>
          <ToggleSwitch
            checked={testEnabled}
            disabled={saving}
          >
            <input 
              type="checkbox" 
              checked={testEnabled} 
              onChange={(e) => updateSetting('test_payments_enabled', e.target.checked)}
              disabled={saving}
            />
            <span className="slider"></span>
          </ToggleSwitch>
        </SettingRow>

        <InfoBox>
          <FiShield />
          <div>
            <strong>🔐 Admin Access Only:</strong> Test payments are restricted to users with administrator role. 
            Regular users will only see PayPal as a payment option.
          </div>
        </InfoBox>
      </Card>

      {/* Active Payment Methods Summary */}
      <Card>
        <CardHeader>
          <CardTitle>
            ✅ Active Payment Methods
          </CardTitle>
        </CardHeader>

        <ActiveMethods>
          {paypalEnabled && runtime.paypal_configured && (
            <MethodBadge>
              <FiCreditCard />
              PayPal ({paypalMode})
            </MethodBadge>
          )}
          {testEnabled && (
            <MethodBadge test>
              🧪 Test Payments (Admin Only)
            </MethodBadge>
          )}
          {!paypalEnabled && !testEnabled && (
            <EmptyState>
              <FiAlertCircle /> No payment methods are currently enabled
            </EmptyState>
          )}
        </ActiveMethods>

        <InfoBox>
          <FiShield />
          <div>
            <strong>Security Note:</strong> PayPal credentials (Client ID and Secret) 
            must be configured in the server .env file. Only mode settings can be changed here.
            Test payments are restricted to administrator accounts only.
          </div>
        </InfoBox>
      </Card>
    </Container>
  );
};

export default PaymentSettings;

// Styled Components
const Container = styled.div`
  padding: 24px;
  max-width: 1000px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: #3b82f6;
  }
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #f3f4f6;
  border: none;
  border-radius: 8px;
  color: #374151;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #e5e7eb;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const CardTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const StatusBadge = styled.div<{ status: string }>`
  padding: 6px 12px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 600;
  background: ${props => {
    const colors: {[key: string]: string} = {
      connected: '#d1fae5',
      active: '#d1fae5',
      inactive: '#f3f4f6',
      notConfigured: '#fee2e2'
    };
    return colors[props.status] || '#f3f4f6';
  }};
  color: ${props => {
    const colors: {[key: string]: string} = {
      connected: '#065f46',
      active: '#065f46',
      inactive: '#374151',
      notConfigured: '#991b1b'
    };
    return colors[props.status] || '#374151';
  }};
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 0;
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingLabel = styled.div`
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 4px;
`;

const SettingDescription = styled.div`
  font-size: 14px;
  color: #6b7280;
`;

const ToggleSwitch = styled.label<{ checked: boolean; disabled?: boolean }>`
  position: relative;
  display: inline-block;
  width: 52px;
  height: 28px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};

  input {
    opacity: 0;
    width: 0;
    height: 0;
  }

  .slider {
    position: absolute;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${props => props.checked ? '#10b981' : '#cbd5e1'};
    transition: 0.3s;
    border-radius: 28px;

    &:before {
      position: absolute;
      content: "";
      height: 20px;
      width: 20px;
      left: ${props => props.checked ? '28px' : '4px'};
      bottom: 4px;
      background-color: white;
      transition: 0.3s;
      border-radius: 50%;
    }
  }
`;

const ModeSelector = styled.div`
  display: flex;
  gap: 8px;
`;

const ModeButton = styled.button<{ active: boolean; warning?: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.active ? (props.warning ? '#ef4444' : '#3b82f6') : '#e5e7eb'};
  background: ${props => props.active ? (props.warning ? '#fef2f2' : '#eff6ff') : 'white'};
  color: ${props => props.active ? (props.warning ? '#ef4444' : '#3b82f6') : '#6b7280'};
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    border-color: ${props => props.warning ? '#ef4444' : '#3b82f6'};
    background: ${props => props.warning ? '#fef2f2' : '#eff6ff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TestButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: #2563eb;
  }

  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const TestResult = styled.div<{ success: boolean }>`
  margin-top: 12px;
  padding: 12px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  background: ${props => props.success ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.success ? '#065f46' : '#991b1b'};
  border: 1px solid ${props => props.success ? '#10b981' : '#ef4444'};
`;

const Divider = styled.div`
  height: 1px;
  background: #e5e7eb;
  margin: 16px 0;
`;

const ActiveMethods = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding: 16px 0;
`;

const MethodBadge = styled.div<{ test?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: ${props => props.test ? '#fef3c7' : '#dbeafe'};
  color: ${props => props.test ? '#92400e' : '#1e40af'};
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
`;

const EmptyState = styled.div`
  padding: 20px;
  text-align: center;
  color: #6b7280;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const InfoBox = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: #eff6ff;
  border: 1px solid #3b82f6;
  border-radius: 8px;
  display: flex;
  gap: 12px;
  font-size: 14px;
  color: #1e40af;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
  }

  strong {
    display: block;
    margin-bottom: 4px;
  }
`;

const Alert = styled.div<{ type: 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.type === 'success' ? '#d1fae5' : '#fee2e2'};
  color: ${props => props.type === 'success' ? '#065f46' : '#991b1b'};
  border: 1px solid ${props => props.type === 'success' ? '#10b981' : '#ef4444'};
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 60px;
  color: #6b7280;

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

// PayPal Status Components
const StatusOverviewCard = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 20px;
  color: white;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
`;

const StatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const StatusTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
`;

const StatusIndicator = styled.div<{ status: string }>`
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => {
    if (props.status === 'connected') return 'rgba(16, 185, 129, 0.2)';
    if (props.status === 'checking') return 'rgba(255, 255, 255, 0.2)';
    return 'rgba(239, 68, 68, 0.2)';
  }};
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);

  .spin {
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const StatusItem = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 16px;
  border-radius: 8px;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const StatusLabel = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 0.5px;
`;

const StatusValue = styled.div<{ status: string }>`
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${props => {
    if (props.status === 'good') return '#10b981';
    if (props.status === 'bad') return '#ef4444';
    if (props.status === 'warning') return '#f59e0b';
    return 'white';
  }};
`;

const StatusError = styled.div`
  margin-top: 16px;
  padding: 12px;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: white;
`;


